import React, { Fragment } from 'react';

import { CommunityBlock as DumbCommunityBlock } from '@lumapps/communities/components/CommunityBlock/CommunityBlock';
import { CommunityVisibility } from '@lumapps/communities/components/CommunityVisibility';
import { ContentBlock as DumbContentBlock } from '@lumapps/contents/components/ContentBlock/ContentBlock';
import { getGroupAll, getGroupPublic } from '@lumapps/customer/ducks/selectors';
import {
    isPageNode,
    NavigationItemType,
    NavigationMap,
    NavigationMapItem,
    NavigationMapPageReferenceType,
} from '@lumapps/navigation/types';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, TranslateObject, useTranslate } from '@lumapps/translations';

/** Refer to the types in `NavigationItemDetails` */
export interface ContentNavigationItemDetailsProps {
    instance?: { id?: string; slug?: string; name: string };
    node: NavigationMapItem;
    pageType?: NavigationMapPageReferenceType;
    pageId?: string;
    translatedTitle?: TranslateObject;
    visibleByGroupReferences: NavigationMap['visibleByGroupReferences'];
    nodeVisibilityFeeds?: { id: string; name: string }[];
    displayPageReferenceData?: boolean;
    isDisabled?: boolean;
}

/**
 * Navigation Item Details for Content Block. It is used both in the admin main page and in the add/edit dialog.
 * In the add/edit dialog, the Details will display the original title and visibility instead of the node's
 *
 * There are some specific variation for community block
 */
export const ContentNavigationItemDetails: React.FC<ContentNavigationItemDetailsProps> = ({
    instance,
    node,
    pageType,
    pageId,
    translatedTitle,
    visibleByGroupReferences,
    nodeVisibilityFeeds,
    displayPageReferenceData,
    isDisabled,
}) => {
    const { translateKey } = useTranslate();

    // All and Public Feeds for specific management regarding these 2 groups
    const feedAll = useSelector(getGroupAll);
    const feedPublic = useSelector(getGroupPublic);

    /**
     * Use visibleByGroupReferences to set
     * the list of groups (=feeds) for which this content is visible.
     */
    const pageVisibilityFeeds = visibleByGroupReferences
        ? node.pageReference?.groupIds?.flatMap((visibleById: string) => ({
              id: visibleById,
              name: visibleByGroupReferences[visibleById],
          }))
        : undefined;

    /**
     * If we don't force display the page reference data, check whether to change the additionalInfoOverride.
     * Do these checks only if it's a page different than Article, and if the node has some visibilities set
     */
    let pageAdditionalInfoOverride;
    if (
        !displayPageReferenceData &&
        isPageNode(node) &&
        pageType !== NavigationItemType.ARTICLE &&
        node.visibleBy &&
        node.visibleBy.length > 0
    ) {
        /**
         * Manage Public NODE case:
         * - If the node has PUBLIC visibility, then force display `visibility: Public`
         */
        if (feedPublic?.id && node.visibleBy.includes(feedPublic.id)) {
            pageAdditionalInfoOverride = [
                <Fragment key="feeds">
                    {translateKey(GLOBAL.VISIBILITY)}:&nbsp;{translateKey(feedPublic.name)}
                </Fragment>,
            ];
        } else if (
            /**
             * Manage Public PAGE case:
             * - If resource page contains visibility PUBLIC and node has ALL visibility, restrict to page's visibilities without public
             * - If no other visibility is available, display "Restricted to All"
             */
            feedPublic?.id &&
            feedAll?.id &&
            node.pageReference?.groupIds?.includes(feedPublic.id) &&
            node.visibleBy.includes(feedAll.id) &&
            nodeVisibilityFeeds &&
            nodeVisibilityFeeds.length > 0
        ) {
            const pageVisibilityWithoutPublic = pageVisibilityFeeds?.filter((feed) => feed.id !== feedPublic.id);

            pageAdditionalInfoOverride = [
                <Fragment key="feeds">
                    {translateKey(GLOBAL.RESTRICTED_TO)}:&nbsp;
                    {pageVisibilityWithoutPublic && pageVisibilityWithoutPublic.length > 0
                        ? pageVisibilityWithoutPublic?.map((feed) => translateKey(feed.name)).join(', ')
                        : translateKey(feedAll.name)}
                </Fragment>,
            ];
        } else if (
            /**
             * Manage other cases:
             *  - If the node's has set visibilities that are not "ALL",
             *  Then change wording from "visibility" to "Restricted to" and display the node's visibility
             */
            feedAll?.id &&
            !node.visibleBy.includes(feedAll.id) &&
            nodeVisibilityFeeds &&
            nodeVisibilityFeeds.length > 0
        ) {
            pageAdditionalInfoOverride = [
                <Fragment key="feeds">
                    {translateKey(GLOBAL.RESTRICTED_TO)}:&nbsp;
                    {nodeVisibilityFeeds.map((feed) => translateKey(feed.name)).join(', ')}
                </Fragment>,
            ];
        }

        /** If there is some override, add the other information */
        if (pageAdditionalInfoOverride) {
            /* Add the instance name if available */
            if (instance?.name) {
                pageAdditionalInfoOverride.push(
                    <Fragment key="instance">
                        {translateKey(GLOBAL.SOURCE)}:&nbsp;
                        {instance.name}
                    </Fragment>,
                );
            }

            /** If the node is a community and has privacy, add it at the beginning of the array */
            if (node.pageReference?.privacy) {
                pageAdditionalInfoOverride.unshift(
                    <CommunityVisibility key="visibility" visibility={node.pageReference.privacy} />,
                );
            }
        }
    }

    return pageType === NavigationItemType.COMMUNITY ? (
        <DumbCommunityBlock
            community={{
                title: translatedTitle,
                thumbnail: node.thumbnailUrl,
                privacy: node.pageReference?.privacy,
            }}
            instance={instance}
            variant="small"
            isDisabled={isDisabled}
            additionalInfoOverride={pageAdditionalInfoOverride}
        />
    ) : (
        <DumbContentBlock
            content={{ id: pageId, title: translatedTitle }}
            variant="small"
            isDisabled={isDisabled}
            feeds={pageVisibilityFeeds}
            customContentType={node.contentType}
            instance={instance}
            thumbnailUrl={node.thumbnailUrl}
            additionalInfoOverride={pageAdditionalInfoOverride}
        />
    );
};
